import { pick } from 'lodash';

import { formatToOnlyDigit } from 'commonUtils/formatters/formatToOnlyDigit';

import type { TLoansRestoredData } from 'shared/types/TCustomerService';

import { convertJobTitleField } from 'entities/creditForm/lib/mappersToCreditForm/convertJobTitleField';
import { mapCarAndPropertyAvailability } from 'entities/creditForm/lib/mappersToCreditForm/mapCarAndPropertyAvailability';

import type {
  TAdditionalDataFormFields,
  TContactsFormFields,
  TCreditFormCommonFields,
  TCreditFormEntityReducer,
  TCreditPurposeFormFields,
  TPassportFormFields,
  TWorkplaceFormFields,
} from '../../types';

import { convertAddressField } from './convertAddressField';
import { convertCustomerAmountValue } from './convertCustomerAmountValue';
import { convertDictionaryField } from './convertDictionaryField';
import { convertFullNameField } from './convertFullNameField';
import { makeLastWorkExperienceStartMonthFieldValue } from './makeLastWorkExperienceStartMonthFieldValue';
import { makeRegionFieldValue } from './makeRegionFieldValue';
import { mapCustomerAutoFieldsToCreditForm } from './mapCustomerAutoFieldsToCreditForm';

export const mapLoansRestoreDataToCreditForm = (
  state: TCreditFormEntityReducer,
  data: Partial<TLoansRestoredData['customer']>,
) => {
  const { creditPurposeForm, contactsForm, passportForm, workplaceForm, additionalDataForm, dictionaries } = state;

  const formData: TCreditFormCommonFields = {
    ...mapCustomerAutoFieldsToCreditForm(state, data),
    ...mapCarAndPropertyAvailability(state, data),
    amount: convertCustomerAmountValue(data.amount),
    period: convertDictionaryField(data.term, 'period', dictionaries) as TCreditFormCommonFields['period'],
    purpose: convertDictionaryField(data.purpose, 'purpose', dictionaries) as TCreditFormCommonFields['purpose'],
    region: makeRegionFieldValue(data.region),
    mobilePhone: formatToOnlyDigit(data.mobilePhone),
    email: data.email || '',
    userAgreement: true,
    advertisingAgreement: true,
    fullName: convertFullNameField(data.fullName, data.gender),
    gender: data.gender ?? 'Unknown',
    birthDate: data.birthDate || '',
    birthPlace: data.birthPlace || '',
    passportNumber: formatToOnlyDigit(data.passportNumber),
    passportIssueDate: data.passportIssueDate || '',
    passportIssuedBy: data.passportIssuedBy || '',
    unitCode: data.unitCode || '',
    registrationAddress: convertAddressField(data.registrationAddress),
    registrationAddressFlat: '',
    registrationDate: data.registrationDate || '',
    residenceAddress: convertAddressField(data.residenceAddress),
    residenceAddressFlat: '',
    residenceAddressIsRegistration: data.registrationAddress?.dadata === data.residenceAddress?.dadata,
    employmentType: convertDictionaryField(
      data.employmentType,
      'employmentType',
      dictionaries,
    ) as TCreditFormCommonFields['employmentType'],
    unemploymentReason: convertDictionaryField(
      data.unemploymentReason,
      'unemploymentReason',
      dictionaries,
    ) as TCreditFormCommonFields['unemploymentReason'],
    anotherUnemploymentReason: data.anotherUnemploymentReason || '',
    monthlySalary: data.monthlySalary ? String(data.monthlySalary) : '',
    areaOfEmployment: convertDictionaryField(
      data.areaOfEmployment,
      'areaOfEmployment',
      dictionaries,
    ) as TCreditFormCommonFields['areaOfEmployment'],
    // TODO обновить swagger customer service, чтобы там был okved
    okved: (data as unknown as Record<string, never>).okved || '',
    employerType: convertDictionaryField(
      data.employerType,
      'employerType',
      dictionaries,
    ) as TCreditFormCommonFields['employerType'],
    workAddress: convertAddressField(data.workAddress),
    workOrganizationName: data.workOrganizationName || '',
    workPhone: formatToOnlyDigit(data.workPhone),
    jobTitle: convertJobTitleField(data),
    lastWorkExperienceStartMonth: makeLastWorkExperienceStartMonthFieldValue(
      data.lastWorkExperienceStartMonth,
      data.lastWorkExperienceStartYear,
    ),
    employerInn: formatToOnlyDigit(data.employerInn),
    education: convertDictionaryField(
      data.education,
      'education',
      dictionaries,
    ) as TCreditFormCommonFields['education'],
    maritalStatus: convertDictionaryField(
      data.maritalStatus,
      'maritalStatus',
      dictionaries,
    ) as TCreditFormCommonFields['maritalStatus'],
    numberOfDependents: convertDictionaryField(
      data.numberOfDependents,
      'numberOfDependents',
      dictionaries,
    ) as TCreditFormCommonFields['numberOfDependents'],
    additionalPhone: formatToOnlyDigit(data.additionalPhone),
    additionalPhoneOwner: convertDictionaryField(
      data.additionalPhoneOwner,
      'additionalPhoneOwner',
      dictionaries,
    ) as TCreditFormCommonFields['additionalPhoneOwner'],
    additionalPhoneOwnerFullName: convertFullNameField(data.additionalPhoneOwnerFullName),
    propertyTypeAvailability: convertDictionaryField(
      data.propertyTypeAvailability,
      'propertyTypeAvailability',
      dictionaries,
    ) as TCreditFormCommonFields['propertyTypeAvailability'],
    snils: formatToOnlyDigit(data.snils),
  };

  return {
    creditPurposeData: pick(formData, Object.keys(creditPurposeForm.defaults)) as TCreditPurposeFormFields,
    contactsData: pick(formData, Object.keys(contactsForm.defaults)) as TContactsFormFields,
    passportData: pick(formData, Object.keys(passportForm.defaults)) as TPassportFormFields,
    workplaceData: pick(formData, Object.keys(workplaceForm.defaults)) as TWorkplaceFormFields,
    additionalData: pick(formData, Object.keys(additionalDataForm.defaults)) as TAdditionalDataFormFields,
  };
};
